import { isRequestCanceled } from '../../utils/errors';
import { Report } from '../../utils/types';
import { StateCreator } from 'zustand';
import moment from 'moment/moment';
import i18n from '../../../i18n';
import { axiosInstance } from '../../utils/axiosApp';
import {
  isFinalizationPendingReport,
  isTransientSessionReport,
} from '../../utils/utils';

const LIMIT = 10;
const REQUEST_TIMEOUT_MS = 15_000;
type SessionLoadMode = 'load' | 'retry' | 'refresh';

export interface SessionTranslations {
  inProgress: string;
  finalizationPending: string;
  finalizationPendingDuration: string;
}

export const sessionTranslations: SessionTranslations = {
  // Initialized with keys to avoid translating before i18n resources are loaded.
  inProgress: 'sessionsList.inProgress',
  finalizationPending: 'sessionsList.finalizationPending',
  finalizationPendingDuration: 'sessionsList.finalizationPendingDuration',
};

interface CurrentSessionResponse {
  session_number: number | null;
}

interface SessionStartStatusResponse {
  ready: boolean;
  finalizing_session_number: number | null;
}

interface SessionListStatus {
  report: Report | null;
  hasTransientSession: boolean;
}

interface MetadataRequest {
  pageLimit: number;
  maxPage: number;
  hasTransientSession: boolean;
  sortKey: keyof Report;
  sortDirection: string;
  page?: number;
}

interface TransientSessionPlacement {
  currentPage: number;
  maxPage: number;
  sortDirection: string;
}

interface DurationTranslations {
  ongoing: string;
  oldOngoing: string;
  hour: string;
  oldHour: string;
  minute: string;
  oldMinute: string;
  second: string;
  oldSecond: string;
}

export const durationTranslations: DurationTranslations = {
  // Initialized with keys to avoid translating before i18n resources are loaded.
  ongoing: 'duration.ongoing',
  oldOngoing: 'duration.ongoing',
  hour: 'duration.hour',
  oldHour: 'duration.hour',
  minute: 'duration.minute',
  oldMinute: 'duration.minute',
  second: 'duration.second',
  oldSecond: 'duration.second',
};

export interface ProfileSessionState {
  sessions: Report[];
  sessionRequest: {
    key: string;
    status: 'loading' | 'error' | 'loaded';
  } | null;
  sessionSnapshot: {
    profileId: string;
    queryKey: string;
    page: number;
  } | null;
  length: number | null;
  hasTransientSession: boolean;
  fetchedPages: Record<number, Report[]>;
  currentPage: number;
  pageLimit: number;
  maxPage: number;
  sortKey: keyof Report;
  sortDirection: string;
  currentSession: Report | null;
  isMobile: boolean;

  setIsMobile: (isMobile: boolean) => void;
  setSortKey: (key: keyof Report) => void;
  setSortDirection: (direction: 'ASC' | 'DESC') => void;
  setPageLimit: (limit: number) => void;
  setCurrentPage: (page: number) => void;

  setSessions: (sessions: Report[]) => void;
  clearSessions: () => void;
  removeStoppedSession: (profileId: string, sessionNumber: number) => void;

  fetchSessionData: (
    profileId: string,
    mode?: SessionLoadMode
  ) => Promise<void>;
  cancelSessionData: () => void;
  refreshFinalizationPendingSession: (profileId: string) => Promise<void>;
  refreshSessionTranslations: () => void;
  refreshDurationTranslations: () => void;
}

type ProfileSessionStateWithActiveProfile = ProfileSessionState & {
  activeProfileId?: string | null;
};

export const sessionQueryKey = (
  profileId: string,
  state: ProfileSessionState
) =>
  JSON.stringify([
    profileId,
    state.pageLimit,
    state.sortKey,
    state.sortDirection,
  ]);

export const sessionPageKey = (profileId: string, state: ProfileSessionState) =>
  JSON.stringify([sessionQueryKey(profileId, state), state.currentPage]);

export const createProfileSessionStore: StateCreator<ProfileSessionState> = (
  set,
  get
) => {
  const formatDuration = (duration: string | null) => {
    if (duration === null) {
      return durationTranslations.ongoing;
    }

    const [hours, minutes, seconds] = duration.split(':').map(Number);
    const d = moment.duration({ hours, minutes, seconds });

    let formatted = '';

    if (d.hours() > 0) {
      const h = d.hours();
      formatted += `${h} ${i18n.t('duration.hour', { count: h })} `;
    }

    if (d.minutes() > 0) {
      const m = d.minutes();
      formatted += `${m} ${i18n.t('duration.minute', { count: m })} `;
    }

    if (!formatted.trim()) {
      const s = d.seconds();
      formatted = `${s} ${i18n.t('duration.second', { count: s })}`;
    }

    return formatted.trim();
  };

  const buildCurrentSessionReport = (
    currentSession: CurrentSessionResponse
  ): Report | null => {
    if (currentSession.session_number != null) {
      return {
        session_number: currentSession.session_number,
        title: sessionTranslations.inProgress,
        created_at: null,
        duration: formatDuration(null),
        status: 'in_progress',
      };
    }

    return null;
  };

  const buildFinalizationPendingReport = (
    startStatus: SessionStartStatusResponse
  ): Report | null => {
    if (!startStatus.ready && startStatus.finalizing_session_number != null) {
      return {
        session_number: startStatus.finalizing_session_number,
        title: sessionTranslations.finalizationPending,
        created_at: null,
        duration: sessionTranslations.finalizationPendingDuration,
        status: 'finalization_pending',
      };
    }

    return null;
  };

  const fetchCurrentSessionReport = async (
    profileId: string,
    signal: AbortSignal
  ): Promise<Report | null> => {
    const response = await axiosInstance().get<CurrentSessionResponse>(
      `/api/profiles/${profileId}/sessions/current/number`,
      { signal, timeout: REQUEST_TIMEOUT_MS }
    );
    return buildCurrentSessionReport(response.data);
  };

  const fetchFinalizationPendingReport = async (
    profileId: string,
    signal: AbortSignal
  ): Promise<Report | null> => {
    const response = await axiosInstance().get<SessionStartStatusResponse>(
      `/api/profiles/${profileId}/sessions/start-status`,
      { signal, timeout: REQUEST_TIMEOUT_MS }
    );
    return buildFinalizationPendingReport(response.data);
  };

  const fetchSessionListStatus = async (
    profileId: string,
    signal: AbortSignal
  ): Promise<SessionListStatus> => {
    // Both reads must succeed before replacing the snapshot. A failed read
    // cannot establish that there is no unfinished session.
    const [currentReport, pendingReport] = await Promise.all([
      fetchCurrentSessionReport(profileId, signal),
      fetchFinalizationPendingReport(profileId, signal),
    ]);
    const report = currentReport ?? pendingReport;
    return {
      report,
      hasTransientSession: report !== null,
    };
  };

  const isProfileRequestCurrent = (profileId: string) => {
    const state = get() as ProfileSessionStateWithActiveProfile;
    return state.activeProfileId === profileId;
  };

  const formatMetadataReports = (reports: Report[]): Report[] =>
    reports.map((report) => ({
      ...report,
      session_number: report.session_number,
      duration: formatDuration(report.duration),
    }));

  const fetchMetadataReports = async (
    profileId: string,
    request: MetadataRequest,
    signal: AbortSignal
  ): Promise<Report[]> => {
    const params = new URLSearchParams({
      max_page: String(request.maxPage),
      limit: String(request.pageLimit),
      has_new_session: String(request.hasTransientSession),
      sort_key: String(request.sortKey),
      sort_direction: request.sortDirection,
    });
    if (request.page != null) {
      params.set('page', String(request.page));
    }

    const response = await axiosInstance().get(
      `/api/profiles/${profileId}/sessions/notes/metadata?${params.toString()}`,
      { signal, timeout: REQUEST_TIMEOUT_MS }
    );
    return formatMetadataReports(response.data.metadata);
  };

  const addTransientSessionReport = (
    reports: Report[],
    sessionListStatus: SessionListStatus,
    placement: TransientSessionPlacement
  ): Report[] => {
    if (!sessionListStatus.report) {
      return reports;
    }
    if (placement.currentPage === 1 && placement.sortDirection === 'DESC') {
      return [sessionListStatus.report, ...reports];
    }
    if (
      placement.currentPage === placement.maxPage &&
      placement.sortDirection === 'ASC'
    ) {
      return [...reports, sessionListStatus.report];
    }
    return reports;
  };

  let activeRequest: {
    key: string;
    controller: AbortController;
    promise: Promise<void>;
  } | null = null;

  const cancelSessionData = () => {
    activeRequest?.controller.abort();
    activeRequest = null;
    if (get().sessionRequest?.status === 'loading') {
      set({ sessionRequest: null });
    }
  };

  const loadSessionPage = (
    profileId: string,
    mode: SessionLoadMode = 'load'
  ): Promise<void> => {
    if (!isProfileRequestCurrent(profileId)) return Promise.resolve();
    if (mode === 'refresh') {
      cancelSessionData();
      set({ currentPage: 1, sortKey: 'session_number', sortDirection: 'DESC' });
    }
    const request = get();
    const key = sessionPageKey(profileId, request);
    const queryKey = sessionQueryKey(profileId, request);
    if (activeRequest?.key === key) return activeRequest.promise;
    if (
      mode === 'load' &&
      request.sessionRequest?.key === key &&
      request.sessionRequest.status === 'error'
    ) {
      return Promise.resolve();
    }

    cancelSessionData();
    const controller = new AbortController();
    const token = { key, controller, promise: Promise.resolve() };
    activeRequest = token;
    const isCurrent = () =>
      activeRequest === token &&
      !controller.signal.aborted &&
      isProfileRequestCurrent(profileId) &&
      sessionPageKey(profileId, get()) === key;
    set({
      sessionRequest: { key, status: 'loading' },
      // Keep this profile's last successful snapshot until its replacement is
      // ready, even when Refresh changes the sort. Never retain another profile.
      ...(request.sessionSnapshot?.profileId !== profileId
        ? {
            sessions: [],
            fetchedPages: {},
            sessionSnapshot: null,
            currentSession: null,
            hasTransientSession: false,
            length: null,
            maxPage: 1,
          }
        : {}),
    });

    token.promise = (async () => {
      try {
        const [count, status] = await Promise.all([
          axiosInstance().get(`/api/profiles/${profileId}/sessions/count`, {
            signal: controller.signal,
            timeout: REQUEST_TIMEOUT_MS,
          }),
          fetchSessionListStatus(profileId, controller.signal),
        ]);
        if (!isCurrent()) return;
        // The API returns the latest zero-based session number, including a
        // current/finalizing session, rather than the number of saved reports.
        const latestSessionNumber = count.data.sessions_count;
        const length =
          latestSessionNumber === null ? 0 : latestSessionNumber + 1;
        const maxPage = Math.max(1, Math.ceil(length / request.pageLimit));
        // An expired bookmark or a removed final page selects the last valid page.
        if (request.currentPage > maxPage) {
          set({ length, maxPage, currentPage: maxPage });
          return;
        }
        set({ length, maxPage });
        const reports = await fetchMetadataReports(
          profileId,
          {
            pageLimit: request.pageLimit,
            maxPage,
            page: request.currentPage,
            hasTransientSession: status.hasTransientSession,
            sortKey: request.sortKey,
            sortDirection: request.sortDirection,
          },
          controller.signal
        );
        if (!isCurrent()) return;
        const pageReports = addTransientSessionReport(reports, status, {
          currentPage: request.currentPage,
          maxPage,
          sortDirection: request.sortDirection,
        });
        set((state) => {
          // New queries and fresh first pages replace the old pagination
          // snapshot. Only pages from the same successful query can be merged.
          const replacePages =
            request.currentPage === 1 ||
            state.sessionSnapshot?.queryKey !== queryKey;
          const cachedPages = replacePages
            ? {}
            : Object.fromEntries(
                Object.entries(state.fetchedPages).map(([page, rows]) => [
                  page,
                  addTransientSessionReport(
                    rows.filter((row) => !isTransientSessionReport(row)),
                    status,
                    {
                      currentPage: Number(page),
                      maxPage,
                      sortDirection: request.sortDirection,
                    }
                  ),
                ])
              );
          const fetchedPages: Record<number, Report[]> = {
            ...cachedPages,
            [request.currentPage]: pageReports,
          };
          // Use cached page order to keep equal sort values in API order.
          const reportsByNumber = new Map<number, Report>();
          const isSavedReport = (report: Report) =>
            !isTransientSessionReport(report) &&
            report.session_number !== status.report?.session_number;
          for (const page of Object.keys(fetchedPages)
            .map(Number)
            .sort((a, b) => a - b)) {
            for (const report of fetchedPages[page]) {
              if (isSavedReport(report)) {
                reportsByNumber.set(report.session_number, report);
              }
            }
          }
          // Revalidating an offset page can move an already-loaded report
          // outside that page. Retain loaded reports and their newest metadata
          // until an explicit snapshot replacement.
          if (!replacePages) {
            for (const report of state.sessions) {
              if (isSavedReport(report)) {
                reportsByNumber.set(report.session_number, report);
              }
            }
          }
          // Fresh metadata wins over stale copies on overlapping cached pages.
          for (const report of reports) {
            if (isSavedReport(report))
              reportsByNumber.set(report.session_number, report);
          }
          const sessions = Array.from(reportsByNumber.values()).sort((a, b) => {
            const left = a[request.sortKey] ?? null;
            const right = b[request.sortKey] ?? null;
            if (left === right) return 0;
            const order =
              left === null ? -1 : right === null ? 1 : left < right ? -1 : 1;
            return request.sortDirection === 'ASC' ? order : -order;
          });
          // Keep the unfinished session visible even before its page loads.
          if (status.report) {
            if (request.sortDirection === 'DESC')
              sessions.unshift(status.report);
            else sessions.push(status.report);
          }
          return {
            sessionSnapshot: { profileId, queryKey, page: request.currentPage },
            currentSession: status.report,
            hasTransientSession: status.hasTransientSession,
            sessions,
            fetchedPages,
            sessionRequest: { key, status: 'loaded' },
          };
        });
      } catch (error) {
        if (isCurrent() && !isRequestCanceled(error)) {
          set({ sessionRequest: { key, status: 'error' } });
        }
      } finally {
        controller.abort();
        if (activeRequest === token) {
          activeRequest = null;
          if (get().sessionRequest?.status === 'loading')
            set({ sessionRequest: null });
        }
      }
    })();
    return token.promise;
  };

  return {
    sessions: [],
    sessionRequest: null,
    sessionSnapshot: null,
    length: null,
    hasTransientSession: false,
    fetchedPages: {},
    currentPage: 1,
    pageLimit: LIMIT,
    maxPage: 1,
    sortKey: 'session_number',
    sortDirection: 'DESC',
    currentSession: null,
    isMobile: false,
    setIsMobile: (isMobile: boolean) => {
      set({ isMobile: isMobile });
    },
    setSortKey: (key: keyof Report) => set({ sortKey: key }),
    setSortDirection: (direction: 'ASC' | 'DESC') =>
      set({ sortDirection: direction }),
    setPageLimit: (limit: number) => set({ pageLimit: limit }),
    setCurrentPage: (page: number) => {
      set({ currentPage: page });
    },
    setSessions: (newSessions: Report[]) => {
      set((state) => ({
        sessions: [...state.sessions, ...newSessions],
        fetchedPages: {
          ...state.fetchedPages,
          [state.currentPage]: [
            ...(state.fetchedPages[state.currentPage] || []),
            ...newSessions,
          ],
        },
      }));
    },

    clearSessions: () => {
      cancelSessionData();
      set({
        sessions: [],
        fetchedPages: {},
        sessionRequest: null,
        sessionSnapshot: null,
      });
    },
    removeStoppedSession: (profileId, sessionNumber) =>
      set((state) => {
        if (
          (state as ProfileSessionStateWithActiveProfile).activeProfileId !==
            profileId ||
          state.sessionSnapshot?.profileId !== profileId
        )
          return state;
        const keep = (session: Report) =>
          session.session_number !== sessionNumber ||
          session.status !== 'in_progress';
        const currentSession =
          state.currentSession && keep(state.currentSession)
            ? state.currentSession
            : null;
        return {
          sessions: state.sessions.filter(keep),
          currentSession,
          hasTransientSession: isTransientSessionReport(currentSession),
          fetchedPages: Object.fromEntries(
            Object.entries(state.fetchedPages).map(([page, sessions]) => [
              page,
              sessions.filter(keep),
            ])
          ),
        };
      }),

    fetchSessionData: loadSessionPage,
    cancelSessionData,
    refreshFinalizationPendingSession: async (profileId) => {
      if (
        isFinalizationPendingReport(get().currentSession) &&
        get().sessionRequest?.status !== 'error'
      ) {
        await loadSessionPage(profileId);
      }
    },
    refreshSessionTranslations: () => {
      set((state) => {
        const updatedSessions = state.sessions.map((session) => {
          if (session.status === 'in_progress') {
            return { ...session, title: sessionTranslations.inProgress };
          }
          if (session.status === 'finalization_pending') {
            return {
              ...session,
              title: sessionTranslations.finalizationPending,
              duration: sessionTranslations.finalizationPendingDuration,
            };
          }
          return session;
        });
        const updatedFetchedPages: Record<number, Report[]> = {};
        Object.entries(state.fetchedPages).forEach(([page, sessions]) => {
          updatedFetchedPages[Number(page)] = sessions.map((session) => {
            if (session.status === 'in_progress') {
              return { ...session, title: sessionTranslations.inProgress };
            }
            if (session.status === 'finalization_pending') {
              return {
                ...session,
                title: sessionTranslations.finalizationPending,
                duration: sessionTranslations.finalizationPendingDuration,
              };
            }
            return session;
          });
        });
        return {
          sessions: updatedSessions,
          fetchedPages: updatedFetchedPages,
        };
      });
    },
    refreshDurationTranslations: () => {
      set((state) => {
        const replaceOldDurationStrings = (durStr: string) => {
          if (!durStr) return durStr;
          let replaced = durStr;
          replaced = replaced.replace(
            durationTranslations.oldOngoing,
            durationTranslations.ongoing
          );
          replaced = replaced.replace(
            durationTranslations.oldHour,
            durationTranslations.hour
          );
          replaced = replaced.replace(
            durationTranslations.oldMinute,
            durationTranslations.minute
          );
          replaced = replaced.replace(
            durationTranslations.oldSecond,
            durationTranslations.second
          );
          return replaced;
        };

        const updatedSessions = state.sessions.map((sess) => ({
          ...sess,
          duration: replaceOldDurationStrings(sess.duration || ''),
        }));

        const updatedFetchedPages: Record<number, Report[]> = {};
        for (const [page, reports] of Object.entries(state.fetchedPages)) {
          updatedFetchedPages[Number(page)] = reports.map((rep) => ({
            ...rep,
            duration: replaceOldDurationStrings(rep.duration || ''),
          }));
        }

        return {
          sessions: updatedSessions,
          fetchedPages: updatedFetchedPages,
        };
      });
    },
  };
};
